import type { Href } from "expo-router";
import { router } from "expo-router";

import { supabase } from "./supabase";
import type { AppRole } from "./auth";

/**
 * Admin "view as user" — works across every portal, without ever swapping
 * auth tokens.
 *
 * The admin keeps their own authenticated session. A server-side session row
 * (`admin_impersonation_sessions`) records who is viewing whom, in which mode
 * (read / edit), and gates the whole capability to **super_admin** — enforced
 * in the `admin_impersonation_*` RPCs, not just the UI. Each portal lib read
 * branches from "fetch my own row" to "fetch the target's row by id"; because
 * the admin already has `is_admin()` SELECT access to those tables, no RLS is
 * loosened — the client simply points its queries at the target id.
 *
 * Writes are disabled by default (read-only). An explicit pencil toggle flips
 * the session to edit mode; on-behalf writes then go through dedicated
 * SECURITY DEFINER RPCs that re-check super_admin + an active edit session and
 * audit a before/after diff. Enter / exit / mode / edit are all audited via
 * `log_admin_action('impersonate.*')`.
 */

const STORAGE_KEY = "coravu_impersonating_user_id";
const ROLE_KEY = "coravu_impersonating_role";
const MODE_KEY = "coravu_impersonating_mode";

export type ImpersonationMode = "read" | "edit";

export type ImpersonationContext = {
  userId: string;
  email: string | null;
};

let memoryCtx: ImpersonationContext | null = null;

function readQueryParam(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const sp = new URLSearchParams(window.location.search);
    const v = sp.get("view_as");
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

function readStorage(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (value) window.sessionStorage?.setItem(key, value);
    else window.sessionStorage?.removeItem(key);
  } catch {
    /* storage disabled */
  }
}

/** Returns the impersonated user id, or null when not impersonating. */
export function getImpersonatedUserId(): string | null {
  return readQueryParam() ?? readStorage(STORAGE_KEY) ?? memoryCtx?.userId ?? null;
}

export function isImpersonating(): boolean {
  return getImpersonatedUserId() !== null;
}

/** The target's role (for portal routing / gate relaxation), or null. */
export function getImpersonatedRole(): AppRole | null {
  return (readStorage(ROLE_KEY) as AppRole | null) ?? null;
}

export function getImpersonationMode(): ImpersonationMode {
  return readStorage(MODE_KEY) === "edit" ? "edit" : "read";
}

/**
 * The id reads should scope to: the impersonation target when active, else the
 * real signed-in user. Lets a portal read transparently follow the view-as
 * target.
 */
export async function getEffectiveUserId(): Promise<string | null> {
  const target = getImpersonatedUserId();
  if (target) return target;
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

/**
 * The portal key the active view-as target belongs to (for PortalGate
 * relaxation), or null when not impersonating. Mirrors `portalPathForRole`.
 */
export function impersonationPortalKey(): string | null {
  const role = getImpersonatedRole();
  if (!role) return null;
  switch (role) {
    case "student":
      return "student";
    case "judge":
      return "judge";
    case "client":
    case "manager":
      return "client";
    case "campus_admin":
      return "campus";
    case "admin_licensing":
    case "admin_rm":
    case "super_admin":
      return "admin";
    default:
      return null;
  }
}

/** Map a target role to the portal route it lives in. */
export function portalPathForRole(role: AppRole | string | null): string {
  switch (role) {
    case "student":
      return "/student";
    case "judge":
      return "/judge";
    case "client":
    case "manager":
      return "/client";
    case "campus_admin":
      return "/campus";
    case "admin_licensing":
    case "admin_rm":
    case "super_admin":
      return "/admin";
    default:
      return "/client";
  }
}

/**
 * Begin viewing as `target`. Opens the server session (super_admin-gated +
 * audited), then hard-navigates into the target's portal with `?view_as=` set
 * from frame 1. Returns an error string if the server refuses (e.g. caller is
 * not a super_admin) — the caller surfaces it and stays put.
 */
export async function enterImpersonation(
  target: ImpersonationContext,
): Promise<{ error?: string }> {
  const { data, error } = await supabase.rpc("admin_impersonation_start", {
    p_target_user_id: target.userId,
  });
  if (error) return { error: error.message };

  const row = (Array.isArray(data) ? data[0] : data) as
    | { target_role?: string | null; full_name?: string | null; email?: string | null }
    | undefined;
  const role = row?.target_role ?? null;

  memoryCtx = { userId: target.userId, email: row?.email ?? target.email };
  writeStorage(STORAGE_KEY, target.userId);
  writeStorage(ROLE_KEY, role);
  writeStorage(MODE_KEY, "read");
  clearImpersonateCache();

  const path = portalPathForRole(role);
  if (typeof window !== "undefined") {
    window.location.href = `${path}?view_as=${encodeURIComponent(target.userId)}`;
    return {};
  }
  router.replace(`${path}?view_as=${encodeURIComponent(target.userId)}` as Href);
  return {};
}

/** Flip the live session between read-only and edit-on-behalf. */
export async function setImpersonationMode(
  mode: ImpersonationMode,
): Promise<{ error?: string }> {
  const { error } = await supabase.rpc("admin_impersonation_set_mode", { p_mode: mode });
  if (error) return { error: error.message };
  writeStorage(MODE_KEY, mode);
  return {};
}

export async function exitImpersonation(): Promise<void> {
  try {
    await supabase.rpc("admin_impersonation_end");
  } catch {
    /* closing the session is best-effort; we still clear the client state */
  }
  memoryCtx = null;
  writeStorage(STORAGE_KEY, null);
  writeStorage(ROLE_KEY, null);
  writeStorage(MODE_KEY, null);
  clearImpersonateCache();
  if (typeof window !== "undefined") {
    window.location.href = "/admin";
    return;
  }
  router.replace("/admin" as Href);
}

/**
 * Edit-on-behalf: update the target's display name. Server RPC re-checks
 * super_admin + active edit session and audits the diff. Returns an error
 * string on failure (e.g. session not in edit mode).
 */
export async function updateImpersonatedProfileName(
  fullName: string,
): Promise<{ error?: string }> {
  const target = getImpersonatedUserId();
  if (!target) return { error: "Not viewing as a user" };
  const { error } = await supabase.rpc("admin_impersonation_update_profile", {
    p_target_user_id: target,
    p_full_name: fullName,
  });
  if (error) return { error: error.message };
  return {};
}

/** Cached lookup for the impersonated user's company ids. */
let companyIdsCache: { userId: string; ids: string[] } | null = null;

export async function getImpersonatedCompanyIds(): Promise<string[]> {
  const userId = getImpersonatedUserId();
  if (!userId) return [];
  if (companyIdsCache && companyIdsCache.userId === userId) return companyIdsCache.ids;
  const { data, error } = await supabase.rpc("admin_target_company_ids", { p_target_user_id: userId });
  if (error || !data) {
    if (error) console.warn("admin_target_company_ids failed", error);
    return [];
  }
  const ids = ((data as unknown) as { company_id: string }[]).map((r) => r.company_id).filter(Boolean);
  companyIdsCache = { userId, ids };
  return ids;
}

export function clearImpersonateCache() {
  companyIdsCache = null;
}

export type ImpersonatedTargetMeta = {
  userId: string;
  email: string | null;
  fullName: string | null;
  companyName: string | null;
};

/** Best-effort metadata about the impersonation target for the banner. */
export async function getImpersonatedTargetMeta(): Promise<ImpersonatedTargetMeta | null> {
  const userId = getImpersonatedUserId();
  if (!userId) return null;
  const [{ data: profile }, ids] = await Promise.all([
    supabase.from("user_profiles").select("full_name, email").eq("id", userId).maybeSingle(),
    getImpersonatedCompanyIds(),
  ]);
  let companyName: string | null = null;
  if (ids.length > 0) {
    const { data: company } = await supabase
      .from("companies")
      .select("name")
      .eq("id", ids[0])
      .maybeSingle();
    companyName = (company as { name?: string } | null)?.name ?? null;
  }
  const p = (profile as { full_name?: string | null; email?: string | null } | null) ?? null;
  return {
    userId,
    email: p?.email ?? null,
    fullName: p?.full_name ?? null,
    companyName,
  };
}
